import React, { Component, Fragment } from "react";
import { observer } from "mobx-react";
import isHexColor from "validator/es/lib/isHexColor";

import { HexRgbColor } from "@formant/core/model/HexRgbColor";
import { IImageAnnotation } from "@formant/core/model/IImageAnnotation";
import { Measure } from "@formant/ui/component/common/Measure";

import { ScaledImageCoordinates } from "../../common/ScaledImageCoordinates";
import { AssetImage } from "../AssetImage";
import * as styles from "./AnnotatedImage.scss";

interface IAnnotationsProps {
    annotations: IImageAnnotation[];
    src?: string;
}

@observer
export class AnnotatedImage extends Component<IAnnotationsProps> {
    private scaledImageCoordinates: ScaledImageCoordinates =
        new ScaledImageCoordinates(0, 0, 0, 0);

    public render() {
        const { annotations, src } = this.props;

        if (annotations.length === 0) {
            return false;
        }

        const annotationShapes = annotations.map((annotation, i) => (
            <Fragment key={i}>{this.annotationShape(annotation)}</Fragment>
        ));

        return (
            <Measure
                onMeasureResize={this.scaledImageCoordinates.onResize}
                className={styles["annotated-image"]}
            >
                <AssetImage
                    src={src}
                    onImageSize={this.scaledImageCoordinates.onImageSize}
                />
                <svg
                    width={this.scaledImageCoordinates.width}
                    height={this.scaledImageCoordinates.height}
                >
                    {annotationShapes}
                </svg>
            </Measure>
        );
    }

    private annotationShape = (annotation: IImageAnnotation) => {
        if (annotation.type === "bounding box") {
            return this.boundingBox(annotation);
        }
    };

    private boundingBox = (annotation: IImageAnnotation<"bounding box">) => {
        const rectCoordinates = this.scaledImageCoordinates.scaleBox(
            annotation.area,
        );
        if (!rectCoordinates) {
            return null;
        }

        const { x, y, width, height } = rectCoordinates;

        return (
            <>
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    style={{
                        stroke: this.cssColor(annotation.color),
                        strokeWidth: 1.5,
                        fill: "transparent",
                    }}
                />
                <text
                    x={x + 2}
                    y={y + 2}
                    style={{
                        fill: this.cssColor(annotation.color),
                        fontWeight: "bold",
                        dominantBaseline: "hanging",
                    }}
                >
                    {annotation.label}
                </text>
            </>
        );
    };

    private cssColor = (color: HexRgbColor) =>
        isHexColor(color) ? ("#" + color).replace("##", "#") : "black";
}
